import React from 'react'
import './GridCell.css'
import { useProjectContext } from "../../context/ProjectContext"

const getRotation = (direction) => {
  const dir = String(direction || 'none').toLowerCase().replace(/\s+/g, '-')

  switch (dir) {
    case 'up':
    case 'vertical':
    case 'none':
      return 'rotate(0deg)'
    case 'down':
      return 'rotate(180deg)'
    case 'left':
      return 'rotate(-90deg)'
    case 'right':
    case 'horizontal':
      return 'rotate(90deg)'
    case 'up-right':
    case 'upright':
    case 'right-up':
      return 'rotate(45deg)'
    case 'up-left':
    case 'upleft':
    case 'left-up':
      return 'rotate(-45deg)'
    case 'down-right':
    case 'downright':
    case 'right-down':
    case 'diagonal-right':
      return 'rotate(135deg)'
    case 'down-left':
    case 'downleft':
    case 'left-down':
    case 'diagonal-left':
      return 'rotate(-135deg)'
    default:
      return 'rotate(0deg)'
  }
}

const getLoopHeightPx = () => 16

const getSafeOffset = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

const getSelectionHaloSize = () => {
  return { width: 16, height: 20 }
}

const GridCell = ({
  row,
  col,
  cell,
  dataCell,
  onMouseDown,
  onMouseEnter,
  onClearCell,
  palette,
  previewColor,
  previewOutline,
  previewModeType,
  isSelected,
  onStartDrag,
  isBeingDragged,
}) => {
  const { setSelectedColor, selectedTool } = useProjectContext()

  const handleMouseDown = (e) => {
    if (e.altKey) {
      e.preventDefault()
      e.stopPropagation()

      if (cell?.colorId) {
        const pickedColor = palette?.find?.(
          (p) => String(p?.code) === String(cell.colorId)
        )

        if (pickedColor) {
          setSelectedColor(pickedColor)
        }
      }
      return
    }

    if (
      selectedTool === 'select-stitch' &&
      cell?.colorId &&
      cell?.direction &&
      onStartDrag
    ) {
      e.preventDefault()
      e.stopPropagation()
      onStartDrag(e)
      return
    }

    onMouseDown?.(row, col, e)
  }

  const handleMouseEnter = (e) => {
    onMouseEnter?.(row, col, e)
  }

  const handleContextMenu = (e) => {
    e.preventDefault()
    onClearCell?.(row, col)
  }

  const stitchColor =
    palette?.find?.((p) => String(p?.code) === String(cell?.colorId))?.hex ||
    palette?.find?.((p) => String(p?.code) === String(cell?.colorId))?.color ||
    null

  const offsetX = getSafeOffset(cell?.offsetX)
  const offsetY = getSafeOffset(cell?.offsetY)
  const haloSize = getSelectionHaloSize(cell?.loopHeight)

  return (
    <div
      className={`grid-cell ${isSelected ? 'selected' : ''}`}
      data-cell={dataCell}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onContextMenu={handleContextMenu}
      style={{
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      {previewColor && (
        <div
          className={`cell-preview ${previewModeType || ''}`}
          style={{
            position: 'absolute',
            inset: 0,
            background: previewColor,
            outline: previewOutline ? '1px dashed rgba(0,0,0,0.28)' : 'none',
            pointerEvents: 'none',
          }}
        />
      )}

      {cell?.colorId && stitchColor && isSelected && (
        <div
          className="cell-stitch-selection-halo"
          style={{
            position: 'absolute',
            left: `calc(50% + ${offsetX}px)`,
            top: `calc(50% + ${offsetY}px)`,
            width: `${haloSize.width}px`,
            height: `${haloSize.height}px`,
            transform: `translate(-50%, -50%) ${getRotation(cell?.direction)}`,
            transformOrigin: 'center center',
            borderRadius: '999px',
            border: '1.5px solid rgba(73, 127, 210, 0.95)',
            boxShadow: '0 0 0 4px rgba(73, 127, 210, 0.18)',
            background: 'transparent',
            pointerEvents: 'none',
          }}
        />
      )}

      {cell?.colorId && stitchColor && (
        <div
          className={`cell-stitch ${isBeingDragged ? 'dragging' : ''}`}
          style={{
            position: 'absolute',
            left: `calc(50% + ${offsetX}px)`,
            top: `calc(50% + ${offsetY}px)`,
            width: '8px',
            height: `${getLoopHeightPx(cell?.loopHeight)}px`,
            transform: `translate(-50%, -50%) ${getRotation(cell?.direction)}`,
            transformOrigin: 'center center',
            borderRadius: '999px',
            backgroundColor: stitchColor,
            pointerEvents: 'none',
            boxShadow:
              stitchColor.toLowerCase() === '#ffffff'
                ? '0 0 0 1px rgba(0,0,0,0.18)'
                : 'none',
          }}
        />
      )}
    </div>
  )
}

export default GridCell